import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fullSrc = (src) => (src.startsWith("/api") ? `${BACKEND}${src}` : src);

export const Portfolio = ({ showHeader = true }) => {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/work`);
        setProjects(data);
      } catch {
        setProjects([]);
      }
    })();
  }, []);

  return (
    <section id="work" data-testid="portfolio-section" className="relative py-28 lg:py-40 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {showHeader && (
          <div className="flex flex-wrap items-end justify-between gap-8 mb-16 lg:mb-20">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                Selected Work
              </p>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
                Work that <span className="text-gradient-purple">moves brands.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-xs uppercase tracking-[0.2em] text-[#A6A6B0]/70 max-w-xs">
                A living gallery — new BrandZenith projects land here as they launch.
              </p>
            </Reveal>
          </div>
        )}

        {projects === null ? (
          <p className="text-sm text-[#A6A6B0] py-10">Loading work…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.12} className={i % 2 === 1 ? "md:mt-20" : ""}>
                <article
                  data-testid={`portfolio-card-${i}`}
                  className="group relative overflow-hidden rounded-lg border border-white/[0.06] bg-[#0a0a10] cursor-pointer transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_50px_-14px_rgba(109,0,255,0.55)]"
                >
                  <div className="relative aspect-[16/11] overflow-hidden">
                    <img
                      src={fullSrc(p.src)}
                      alt={`${p.name}${p.category ? ` — ${p.category}` : ""} by BrandZenith`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent"
                    />
                    {p.badge && (
                      <span className="absolute top-5 left-5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8B6FF]/90 bg-black/50 backdrop-blur-sm border border-[#8B2CFF]/30 rounded px-3 py-1.5">
                        {p.badge}
                      </span>
                    )}
                    <div
                      data-testid={`portfolio-view-${i}`}
                      className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#6D00FF] flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500 shadow-[0_0_24px_-4px_rgba(139,44,255,0.9)]"
                      aria-label={`View ${p.name}`}
                    >
                      <ArrowUpRight className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    {p.category && (
                      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#C43CFF] mb-2">
                        {p.category}
                      </p>
                    )}
                    <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mb-2">
                      {p.name}
                    </h3>
                    {p.desc && (
                      <p className="text-sm text-[#A6A6B0] leading-relaxed max-w-md opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                        {p.desc}
                      </p>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
